import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle2, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;



const ProjectDetail = () => {
  const { isDark } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    linkedin: '',
    roles_interested: '',
    reason: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
    
    
    
    
     fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
      // Fallback to mock data
      const mockProject = mockProjects[id];
      if (mockProject) {
        setProject(mockProject);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('join-team-form');
    if (formElement) {
      const elementPosition = formElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100; // 100px offset for navbar
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Add a subtle highlight effect to draw attention
      formElement.style.transition = 'box-shadow 0.3s ease';
      formElement.style.boxShadow = isDark 
        ? '0 0 20px rgba(168, 85, 247, 0.5)' 
        : '0 0 20px rgba(139, 92, 246, 0.3)';
      
      setTimeout(() => {
        formElement.style.boxShadow = '';
      }, 2000);
    } else {
      console.error('Join team form element not found');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
   
    try {
      
     
      await axios.post(`${API}/team-requests`, {
        project_id: id,
        ...formData
      });
      
      setSubmitStatus({
        type: 'success',
        message: 'Application submitted! The team will review and contact you soon.'
      });
      setFormData({ name: '', contact: '', linkedin: '', roles_interested: '', reason: '' });
      
      // Reset success message after 10 seconds (longer visibility)
      setTimeout(() => {
        setSubmitStatus({ type: '', message: '' });
      }, 10000);
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit request. Please try again.'
      });
      // Reset error message after 8 seconds
      setTimeout(() => {
        setSubmitStatus({ type: '', message: '' });
      }, 8000);
    }
  };

  const statusColors = {
    Ideation: { bg: 'bg-yellow-500', text: 'text-white' },
    Building: { bg: 'bg-pink-500', text: 'text-white' },
    Testing: { bg: 'bg-blue-500', text: 'text-white' },
    Live: { bg: 'bg-green-500', text: 'text-white' }
  };

  if (loading) {
    return (
      <div className={`pt-20 min-h-screen flex items-center justify-center transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e]' 
          : 'bg-gradient-to-br from-white via-gray-50 to-purple-50'
      }`}>
        <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className={`pt-20 min-h-screen flex flex-col items-center justify-center transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e]' 
          : 'bg-gradient-to-br from-white via-gray-50 to-purple-50'
      }`}>
        <div className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Project not found</div>
        <Link to="/explore" className="text-purple-400 hover:text-purple-300">
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <motion.div 
      className={`pt-20 min-h-screen transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e]' 
          : 'bg-gradient-to-br from-white via-gray-50 to-purple-50'
      }`}
      data-testid="project-detail-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <motion.div whileHover={{ x: -5 }}>
          <button 
            onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 font-medium transition ${
              isDark 
                ? 'text-purple-300 hover:text-purple-200' 
                : 'text-indigo-600 hover:text-indigo-700'
            }`}
            data-testid="back-to-projects"
          >
            <ArrowLeft size={20} />
            Back
          </button>
        </motion.div>
      </div>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className={`rounded-3xl p-8 border ${
          isDark 
            ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <h1 className={`text-3xl sm:text-4xl font-bold mb-3 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`} data-testid="project-detail-name">{project.name}</h1>
              <p className={`text-lg mb-4 ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`} data-testid="project-detail-tagline">{project.tagline}</p>
            </div>
            
            {/* Status Badge - Top Right */}
            <span className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${statusColors[project.status].bg} ${statusColors[project.status].text}`} data-testid="project-detail-status">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
              {project.status}
            </span>
          </div>
          
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className={`px-4 py-2 rounded-full text-sm font-semibold $${
              isDark 
                ? 'bg-purple-600/20 text-purple-300' 
                : 'bg-indigo-100 text-indigo-800'
            }`} data-testid="project-detail-category">
              {project.domain}
            </span>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${
              isDark 
                ? 'bg-purple-600/20 text-purple-300' 
                : 'bg-purple-100 text-purple-700'
            }`}>
              <Users size={16} />
              {project.team?.length || 0} Team Members
            </span>
          </div>
          
          <div className="flex gap-4">
            <motion.button
              onClick={scrollToForm}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-lg hover:shadow-xl"
              data-testid="join-team-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Join Team
            </motion.button>
            <motion.button
              className={`px-8 py-3 rounded-full font-semibold border-2 transition shadow-lg ${
                isDark 
                  ? 'border-purple-500/50 text-purple-300 hover:bg-purple-600/20' 
                  : 'border-purple-300 text-purple-700 hover:bg-purple-50'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Follow Updates
            </motion.button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Problem Statement and Insights - Full Width */}
        <div className="space-y-8 mb-12">
          {/* Problem Statement */}
          {project.problem_statement && (
            <motion.div 
              className={`rounded-3xl shadow-lg p-6 border ${
                isDark 
                  ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
                  : 'bg-white border-gray-200'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-xl ${
                  isDark ? 'bg-red-500/20' : 'bg-red-100'
                }`}>
                  <svg className={`w-6 h-6 ${
                    isDark ? 'text-red-400' : 'text-red-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className={`text-2xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>Problem Statement</h2>
              </div>
              <p className={`text-base leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`} data-testid="project-detail-problem">{project.problem_statement}</p>
            </motion.div>
          )}

          {/* Insights & Research */}
          {project.insights_research && (
            <motion.div 
              className={`rounded-3xl shadow-lg p-6 border ${
                isDark 
                  ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
                  : 'bg-white border-gray-200'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-xl ${
                  isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                }`}>
                  <svg className={`w-6 h-6 ${
                    isDark ? 'text-blue-400' : 'text-blue-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <h2 className={`text-2xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>Insights & Research</h2>
              </div>
              <p className={`text-base leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`} data-testid="project-detail-insights">{project.insights_research}</p>
            </motion.div>
          )}
        </div>

        {/* Team Section - Full Width */}
        <motion.div 
          className={`rounded-3xl shadow-lg p-6 border mb-12 ${
            isDark 
              ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
              : 'bg-white border-gray-200'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-2 rounded-xl ${
              isDark ? 'bg-pink-500/20' : 'bg-pink-100'
            }`}>
              <Users className={`w-6 h-6 ${
                isDark ? 'text-pink-400' : 'text-pink-600'
              }`} />
            </div>
            <h2 className={`text-2xl font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Team</h2>
          </div>
          {project.team && project.team.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {project.team.map((member, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-2xl ${
                    isDark ? 'bg-purple-900/30' : 'bg-gray-50'
                  }`}
                  data-testid={`team-member-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                      isDark 
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white' 
                        : 'bg-gradient-to-br from-indigo-400 to-purple-400 text-white'
                    }`}>
                      {member.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className={`font-bold ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{member.name}</div>
                      <div className={`text-sm ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>{member.role}</div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isDark 
                        ? 'bg-purple-600/20 text-purple-300' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {member.domain}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-center py-8 ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>No team members yet. Be the first to join!</p>
          )}
        </motion.div>

        {/* Progress Section - Centered */}
        <motion.div 
          className={`rounded-3xl shadow-lg p-8 border mb-12 ${
            isDark 
              ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
              : 'bg-white border-gray-200'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className={`p-2 rounded-xl ${
              isDark ? 'bg-green-500/20' : 'bg-green-100'
            }`}>
              <svg className={`w-6 h-6 ${
                isDark ? 'text-green-400' : 'text-green-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h2 className={`text-2xl font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Progress</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { key: 'phase1_research', label: 'Phase 1', title: 'Research' },
              { key: 'phase2_building', label: 'Phase 2', title: 'Building' },
              { key: 'phase3_testing', label: 'Phase 3', title: 'Testing' },
              { key: 'phase4_showcase', label: 'Phase 4', title: 'Showcase' }
            ].map((phase, index) => (
              <div 
                key={phase.key}
                className={`p-4 rounded-2xl flex flex-col items-center gap-3 text-center ${
                  project.progress?.[phase.key]
                    ? isDark
                      ? 'bg-green-500/20 border-2 border-green-500/30'
                      : 'bg-green-50 border-2 border-green-200'
                    : isDark
                      ? 'bg-gray-800/30'
                      : 'bg-gray-100'
                }`}
                data-testid={`progress-phase${index + 1}`}
              >
                <div className={`flex items-center justify-center w-12 h-12 rounded-full ${
                  project.progress?.[phase.key]
                    ? 'bg-green-500 text-white'
                    : isDark
                      ? 'bg-gray-700 text-gray-400'
                      : 'bg-gray-300 text-gray-500'
                }`}>
                  {project.progress?.[phase.key] ? (
                    <CheckCircle2 size={24} />
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth={2} />
                    </svg>
                  )}
                </div>
                <div>
                  <div className={`font-semibold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{phase.label}</div>
                  <div className={`text-sm ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>{phase.title}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Join Team Form - Below Progress */}
        <motion.div 
          id="join-team-form"
          className={`rounded-3xl shadow-lg p-8 border ${
            isDark 
              ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
              : 'bg-white border-gray-200'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h2 className={`text-2xl font-bold mb-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Join Team</h2>
          <p className={`text-sm mb-6 ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>Interested in contributing to this project? Let us know!</p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-semibold mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your full name"
                required
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition ${
                  isDark 
                    ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-semibold mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>Contact *</label>
              <input
                type="text"
                name="contact"
                value={formData.contact}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition ${
                  isDark 
                    ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-semibold mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>LinkedIn Profile</label>
              <input
                type="url"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleInputChange}
                placeholder="linkedin.com/in/yourprofile"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition ${
                  isDark 
                    ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-semibold mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>Roles Interested In *</label>
              <input
                type="text"
                name="roles_interested"
                value={formData.roles_interested}
                onChange={handleInputChange}
                placeholder="e.g., Developer, Designer, Product Manager"
                required
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition ${
                  isDark 
                    ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-semibold mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>Why do you want to join? *</label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                placeholder="Tell us what excites you about this project..."
                rows={4}
                required
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition ${
                  isDark 
                    ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>


            <motion.button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-lg hover:shadow-xl"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Submit Application
            </motion.button>
          </form>
        </motion.div>
      </div>

      {/* Success/Error Notification Popup */}
      <AnimatePresence>
        {submitStatus.message && (
          <motion.div
            className="fixed bottom-6 right-6 z-50 max-w-sm"
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className={`rounded-2xl p-4 shadow-2xl border-2 ${
                submitStatus.type === 'success'
                  ? isDark
                    ? 'bg-green-600/90 text-white border-green-400/50'
                    : 'bg-green-500 text-white border-green-400'
                  : isDark
                    ? 'bg-red-600/90 text-white border-red-400/50'
                    : 'bg-red-500 text-white border-red-400'
              }`}
            >
              <div className="flex items-center gap-3">
                {submitStatus.type === 'success' ? (
                  <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                <p className="text-sm font-medium">{submitStatus.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProjectDetail;
